import { useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import { Avatar, Box, Button, IconButton, Popover, styled, Typography } from '@mui/material';

import UnfoldMoreIcon from '../assets/buttons-icons/expand-up-down.svg?react';
import palette from '../theme/palette';
import SearchInput from './SearchInput';
import { useAvatar } from './useAvatar';

const ParticipantsFilterButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'isFilterActive'
})<{ isFilterActive: boolean }>(({ isFilterActive }) => ({
  borderRadius: '88px',
  padding: '5px 8px',
  border: `1px solid ${palette.grey[1200]}`,
  boxShadow: 'none',
  backgroundColor: isFilterActive ? palette.grey[500] : 'transparent',
  color: isFilterActive ? palette.white : palette.black,
  fontSize: '12px',
  marginRight: '8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: isFilterActive ? palette.grey[500] : 'transparent',
    boxShadow: 'none'
  },
  '&.Mui-disabled': {
    backgroundColor: 'transparent'
  }
}));

const ParticipantsItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px',
  borderBottom: `1px solid ${palette.grey[300]}`,
  '&:hover': {
    background: `linear-gradient(0deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.02) 100%, ${palette.white})`
  }
}));
const ParticipantsContainer = styled(Box)(() => ({
  maxHeight: '300px',
  overflowY: 'auto'
}));

type ParticipantsFilterProps = {
  handleFilterClick?: () => void;
  onSearch: (value: string) => void;
  onParticipantSelect: (id: string) => void;
  clearFilter: () => void;
  selectedCount: number;
  searchValue: string;
  participants: {
    email?: string;
    status?: string;
    name?: string;
    userId: string;
    phoneNumber?: string;
    isSelected: boolean;
    comment?: string;
  }[];
};

export const ParticipantsFilter = ({
  handleFilterClick,
  participants,
  onParticipantSelect,
  clearFilter,
  selectedCount,
  searchValue,
  onSearch
}: ParticipantsFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { getAvatarStyle } = useAvatar();
  const onFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    handleFilterClick?.();
  };
  const isFilterActive = !!selectedCount;
  const open = Boolean(anchorEl);
  return (
    <>
      <ParticipantsFilterButton
        variant="contained"
        isFilterActive={!!selectedCount}
        onClick={onFilterClick}
      >
        <PeopleOutlinedIcon fontSize="small" sx={{ marginRight: '5px' }} /> Participants
        {isFilterActive && `: ${selectedCount}`}
        {!isFilterActive ? (
          <UnfoldMoreIcon style={{ marginLeft: '5px' }} fontSize="small" />
        ) : (
          <IconButton sx={{ padding: '0px', marginLeft: '5px' }} onClick={clearFilter}>
            <CloseIcon sx={{ color: palette.white, fontSize: '10px' }} />
          </IconButton>
        )}
      </ParticipantsFilterButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              minWidth: 296
            }
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ padding: '8px' }}>
          <SearchInput
            value={searchValue}
            onChange={onSearch}
            placeholder="Search"
            width={'100%'}
            backgroundColor={palette.grey[800]}
          />
        </Box>
        <ParticipantsContainer>
          {participants?.map(participant => {
            const avatarStyle = getAvatarStyle(participant?.userId);
            return (
              <ParticipantsItem
                key={participant?.email}
                onClick={() => participant?.email && onParticipantSelect(participant.email)}
              >
                <Avatar
                  sx={{
                    width: '20px',
                    height: '20px',
                    backgroundColor: avatarStyle.backgroundColor,
                    color: avatarStyle.color,
                    fontSize: '10px'
                  }}
                >
                  <span>
                    {participant?.email?.at(0)?.toUpperCase()}
                    {participant?.email?.at(1)?.toUpperCase()}
                  </span>
                </Avatar>
                <Typography sx={{ flex: 1, fontSize: '12px', color: palette.grey[500] }}>
                  {participant?.email}
                </Typography>
                {participant.isSelected && (
                  <Box sx={{ alignContent: 'center' }}>
                    <CheckIcon sx={{ color: palette.blue[100], fontSize: '0.8rem' }} />
                  </Box>
                )}
              </ParticipantsItem>
            );
          })}
        </ParticipantsContainer>
      </Popover>
    </>
  );
};
